import { useContext, useEffect, useState } from 'react'
import Icon from 'common/Icon'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import format from 'date-fns/format'
import addDays from 'date-fns/addDays'
import subDays from 'date-fns/subDays'
import SlotDateContext from 'context/ProviderBooking/SlotDate'
import Button from 'react-bootstrap/Button'
import { generateDates } from 'app/ProviderSearch/utils'
import { dateFormats } from 'constants/common'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import {
  getCurrentZonnedTime,
  splitDateAndTime,
  isToday,
  getHasFutureSlots,
} from 'utils/common'
import classes from './index.module.scss'
import classNames from 'classnames'
import { NAVY } from 'constants/colors'
import ProviderSlotContext from 'context/ProviderBooking/ProviderSlots'

type DatePaginatorProps = {
  selectedDate: Date
  onDateChange: (date: Date) => void
  className?: string
  btnClassName?: string
  dateColumnClass?: string
  upperDateFormat?: string
  lowerDateFormat?: string
  weekDayContainerClass?: string
}

const DatePaginator: React.FC<DatePaginatorProps> = ({
  className = '',
  btnClassName = '',
  dateColumnClass = '',
  upperDateFormat = '',
  lowerDateFormat = '',
  selectedDate,
  onDateChange,
  weekDayContainerClass = '',
}) => {
  const { slotListLimit } = useContext(SlotDateContext)
  const { timeZone } = useContext(TimeZoneContext)
  const currentDate = getCurrentZonnedTime(timeZone)
  const [dates, setDates] = useState([])
  const { nextAvailabilityForProviders } = useContext(ProviderSlotContext)
  const hasFutureSlots = getHasFutureSlots(nextAvailabilityForProviders || {})

  useEffect(() => {
    if (selectedDate) {
      const dates = generateDates(selectedDate, slotListLimit)
      setDates(dates)
    }
  }, [selectedDate, slotListLimit])

  const maxSlotDate = addDays(currentDate, 20)
  const nextDatesRangeStartDate = addDays(
    selectedDate || new Date(),
    slotListLimit
  )
  const isDisplayedDatePastMaxSlotDate =
    splitDateAndTime(maxSlotDate).date <=
    splitDateAndTime(nextDatesRangeStartDate).date

  const previousSlotDate = () => {
    const previousStartSlotDate = subDays(selectedDate, slotListLimit)

    if (previousStartSlotDate >= currentDate) {
      onDateChange(previousStartSlotDate)
    } else {
      onDateChange(currentDate)
    }
  }

  const nextSlotDate = () => {
    nextDatesRangeStartDate.setHours(0, 0, 0, 0)
    onDateChange(nextDatesRangeStartDate)
  }

  // Timezone format would not be required here because startSlotDate already has the difference calculated for it
  return (
    <div className={`d-flex align-items-center flex-nowrap ${className}`}>
      <Button
        aria-label="show more previous slots"
        variant="none"
        disabled={isToday(selectedDate, timeZone)}
        onClick={previousSlotDate}
        className={classNames(
          classes.date_paginator_button,
          btnClassName,
          'flex-center'
        )}
      >
        <Icon icon="datePrevArrow" className="fa-min-md" fill={NAVY} />
      </Button>
      <Row
        noGutters
        className={classNames(
          'd-none d-lg-flex flex-fill flex-nowrap',
          weekDayContainerClass
        )}
      >
        {dates.map((date: Date) => (
          <Col
            key={date.getTime()}
            className={classNames(
              classes.date_column_container,
              dateColumnClass
            )}
          >
            <div>{format(date, dateFormats.DAY_FORMAT).toUpperCase()}</div>
            <div className="font-weight-medium">
              {lowerDateFormat
                ? format(date, dateFormats[lowerDateFormat])
                : format(date, dateFormats.MONTH_DAY_FORMAT)}
            </div>
          </Col>
        ))}
      </Row>
      <Row className="d-flex d-lg-none flex-fill justify-content-center">
        {dates?.length > 0 && (
          <div className="text-center text-nowrap">
            {upperDateFormat && (
              <div className="fs-14 text-date-light font-weight-bold">
                {format(dates[0], dateFormats[upperDateFormat]).toUpperCase()}
              </div>
            )}
            <div className="fs-18">
              {format(
                dates[0],
                lowerDateFormat
                  ? dateFormats[lowerDateFormat]
                  : dateFormats.MONTH_DAY_FORMAT
              )}
            </div>
          </div>
        )}
      </Row>
      <Button
        aria-label="show more next slots"
        variant="none"
        disabled={isDisplayedDatePastMaxSlotDate && !hasFutureSlots}
        onClick={nextSlotDate}
        className={classNames(
          classes.date_paginator_button,
          btnClassName,
          'flex-center'
        )}
      >
        <Icon icon="dateNextArrow" className="fa-min-md" fill={NAVY} />
      </Button>
    </div>
  )
}

export default DatePaginator
